import { useEffect, useId, useRef, type ReactNode } from "react";
import { AlertTriangle, Inbox, RotateCw, X } from "lucide-react";
import { ApiError, type Coverage } from "../lib/api";

const LABEL: Record<Coverage, string> = { VALIDATED: "Validé", IN_REVIEW: "En relecture", DRAFT: "Brouillon", AI_DRAFT: "Brouillon IA", REJECTED: "À corriger", MISSING: "Manquant" };
export const coverageLabel = (s: Coverage) => LABEL[s] ?? s;

export function Badge({ status, label }: { status: Coverage; label?: string }) {
  return <span className={`badge b-${status}`}>{label ?? LABEL[status] ?? status}</span>;
}

export function errorMessage(e: unknown) {
  if (e instanceof ApiError) return e.message;
  return "Une erreur inattendue s'est produite.";
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="alert err" role="alert">
      <AlertTriangle size={16} />
      <span style={{ flex: 1 }}>{errorMessage(error)}</span>
      {onRetry && <button className="btn sm ghost" onClick={onRetry}><RotateCw size={14} />Réessayer</button>}
    </div>
  );
}

export function Loading({ lines = 4 }: { lines?: number }) {
  return <div className="stack" aria-busy="true" aria-label="Chargement">{Array.from({ length: lines }, (_, i) => <div key={i} className="skeleton" style={{ width: `${90 - i * 12}%` }} />)}</div>;
}

export function PageHead({ kicker, title, lead, children }: { kicker?: ReactNode; title: string; lead?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div style={{ minWidth: 0 }}>
        {kicker && <div className="kicker">{kicker}</div>}
        <h1>{title}</h1>
        {lead && <p className="lead">{lead}</p>}
      </div>
      {children && <div className="actions">{children}</div>}
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return <label className="field"><span>{label}</span>{children}{hint && <small>{hint}</small>}</label>;
}

export function Empty({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <span className="icon-tile ink">{icon ?? <Inbox size={20} />}</span>
      <b>{title}</b>
      {children && <span className="small">{children}</span>}
    </div>
  );
}

/** Contrôle segmenté (un seul choix) : boutons à bascule, accessibles au clavier. */
export function Seg<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; count?: number }[]; label: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}{o.count != null && <span className="n">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export type Progress = { ok: number; review: number; draft: number; total: number };
export function ProgressBar({ p, large }: { p: Progress; large?: boolean }) {
  const t = Math.max(1, p.total);
  return (
    <div className={`progress ${large ? "lg" : ""}`} role="img" aria-label={`${p.ok} validées, ${p.review} en relecture, ${p.draft} en brouillon sur ${p.total}`}>
      <span className="p-ok" style={{ flexGrow: p.ok / t }} />
      <span className="p-review" style={{ flexGrow: p.review / t }} />
      <span className="p-draft" style={{ flexGrow: p.draft / t }} />
      <span style={{ flexGrow: Math.max(0, t - p.ok - p.review - p.draft) / t }} />
    </div>
  );
}

export function Ring({ value, size = 56, stroke = 6, children }: { value: number; size?: number; stroke?: number; children?: ReactNode }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(1, value));
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg className="ring" width={size} height={size} aria-hidden>
        <circle className="track" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} />
        <circle className="val" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} strokeDasharray={c} strokeDashoffset={c * (1 - v)} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontFamily: "var(--display)", fontWeight: 700, fontSize: size > 50 ? 13 : 11 }}>{children}</div>
    </div>
  );
}

export const initials = (name?: string | null) => (name ?? "?").replace(/\(.*?\)/g, "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("") || "?";
export function Avatar({ name, small }: { name?: string | null; small?: boolean }) {
  return <span className={`avatar ${small ? "sm" : ""}`} aria-hidden>{initials(name)}</span>;
}

/**
 * Panneau latéral (dialogue modal) : Échap ferme, le focus entre dans le panneau et revient
 * à l'élément d'origine à la fermeture, Tab reste dans le panneau.
 */
export function Drawer({ title, subtitle, icon, onClose, children, wide }: { title: string; subtitle?: ReactNode; icon?: ReactNode; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const el = ref.current;
    el?.querySelector<HTMLElement>("input, select, textarea, button:not([data-close])")?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      if (e.key === "Tab" && el) {
        const f = [...el.querySelectorAll<HTMLElement>("a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex='-1'])")];
        if (!f.length) return;
        const first = f[0]!, last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = overflow; before?.focus?.({ preventScroll: true }); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div ref={ref} className={`drawer ${wide ? "wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby={id}>
        <div className="drawer-head">
          {icon}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id={id} style={{ fontSize: 18 }}>{title}</h2>
            {subtitle && <div className="muted small" style={{ marginTop: 4 }}>{subtitle}</div>}
          </div>
          <button className="icon-btn bordered" data-close onClick={onClose} aria-label="Fermer le panneau"><X size={16} /></button>
        </div>
        <div className="drawer-body">{children}</div>
      </div>
    </>
  );
}

export const fmtDate = (s: string) => new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const RTF = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });
export function timeAgo(s: string | number) {
  const d = (typeof s === "number" ? s : new Date(s).getTime()) - Date.now();
  const abs = Math.abs(d);
  if (abs < 60_000) return "à l'instant";
  if (abs < 3_600_000) return RTF.format(Math.round(d / 60_000), "minute");
  if (abs < 86_400_000) return RTF.format(Math.round(d / 3_600_000), "hour");
  if (abs < 7 * 86_400_000) return RTF.format(Math.round(d / 86_400_000), "day");
  return new Date(typeof s === "number" ? s : s).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

/** Drapeau d'un code pays ISO à deux lettres (« SN » → 🇸🇳). */
export const flagOf = (code: string) => /^[A-Z]{2}$/.test(code) ? String.fromCodePoint(...[...code].map((c) => 0x1f1a5 + c.charCodeAt(0))) : "🌍";
